/** Manages dash charges, dash duration, i-frames, and charge refill gated on stamina state. */
import Phaser from 'phaser';
import { Component } from './Component';
import { ConfigLoader } from '../systems/ConfigLoader';
import { StaminaComponent } from './StaminaComponent';
import { DebugConfig } from '../debug/DebugConfig';
import { DebugLog } from '../debug/DebugLog';
import type { Position } from '../utils/Types';
import { normalize } from '../utils/Math';

/** Tracks dash charges, i-frame windows, and refill timing; emits dash-changed events every frame. */
export class DashComponent implements Component {
  charges: number;
  isDashing: boolean = false;
  hasIFrames: boolean = false;
  dashDirection: Position = { x: 0, y: 0 };

  private dashTimer: number = 0;
  private dashDuration: number;
  private dashSpeed: number;
  private refillTimer: number = 0;
  private refillTime: number;
  private refillRequiresFullStamina: boolean;
  private stamina: StaminaComponent;
  private events: Phaser.Events.EventEmitter;

  // Coyote time tracking: frames since dash ended
  framesSinceDashEnded: number = Infinity;
  private dashAttackCoyoteFrames: number;

  /** Base max charges from config. */
  private baseMaxCharges: number;
  /** Extra charges granted by passives (e.g. Extra Pip). */
  private _bonusMaxCharges: number = 0;

  /** When non-null, overrides the config refillRequiresFullStamina gate (e.g. Perpetual Motion sets false). */
  overrideRefillRequiresFullStamina: boolean | null = null;

  /** When true, startDash() consumes 0 charges instead of 1 (e.g. Slipstream free-dash window). */
  suppressDashChargeCost: boolean = false;

  /** When true, dashing does not break Hide/Stealth (e.g. Ghost Dash). */
  suppressDashBreaksStealth: boolean = false;

  /** When true, dashing does not generate sound-cone detection events (e.g. Ghost Dash). */
  suppressDashGeneratesNoise: boolean = false;

  /** When true, Blink passes through thin walls (1-tile-thick obstacles) (e.g. Phase Dash). */
  blinkIgnoresThinWalls: boolean = false;

  get maxCharges(): number {
    return this.baseMaxCharges + this._bonusMaxCharges;
  }

  /** Effective refill gate: override wins if set, else config default. */
  get effectiveRefillRequiresFullStamina(): boolean {
    return this.overrideRefillRequiresFullStamina ?? this.refillRequiresFullStamina;
  }

  get bonusMaxCharges(): number {
    return this._bonusMaxCharges;
  }

  set bonusMaxCharges(value: number) {
    this._bonusMaxCharges = value;
    this.charges = Math.min(this.charges, this.maxCharges);
    this.events.emit('dash-changed', this.charges, this.maxCharges, this.refillProgress);
  }

  constructor(events: Phaser.Events.EventEmitter, stamina: StaminaComponent) {
    const config = ConfigLoader.movement.dash;
    this.dashSpeed = config.speed;
    this.dashDuration = config.durationMs;
    this.baseMaxCharges = config.charges.base;
    this.charges = this.maxCharges;
    this.refillTime = config.charges.refillTimeMs;
    this.refillRequiresFullStamina = config.charges.refillRequiresFullStamina;
    this.dashAttackCoyoteFrames = config.dashAttackCoyoteFrames;
    this.stamina = stamina;
    this.events = events;
  }

  get canDash(): boolean {
    return this.charges > 0 && !this.isDashing;
  }

  get speed(): number {
    return this.dashSpeed;
  }

  /** True when an attack should resolve as a dash-attack: during an active dash or within coyote frames after one. */
  get inDashAttackWindow(): boolean {
    return this.isDashing || this.framesSinceDashEnded <= this.dashAttackCoyoteFrames;
  }

  /** 0–1 progress toward next charge refill. */
  get refillProgress(): number {
    if (this.charges >= this.maxCharges || this.isDashing) return 0;
    return Math.min(1, this.refillTimer / this.refillTime);
  }

  /** Consume a charge and begin a dash in the given direction, returning false if no charges are available. */
  startDash(dirX: number, dirY: number): boolean {
    if (!this.canDash) return false;

    // Normalize direction
    const norm = normalize(dirX, dirY);
    if (norm.x === 0 && norm.y === 0) return false;

    this.dashDirection.x = norm.x;
    this.dashDirection.y = norm.y;
    if (!DebugConfig.flags.zeroCooldowns && !this.suppressDashChargeCost) {
      this.charges--;
    }
    DebugLog.log('physics', `DASH started: dir=(${norm.x.toFixed(2)},${norm.y.toFixed(2)}) charges=${this.charges}/${this.maxCharges} speed=${this.dashSpeed}`);
    this.events.emit('dash-started', norm.x, norm.y);
    this.events.emit('dash-changed', this.charges, this.maxCharges);
    this.isDashing = true;
    this.hasIFrames = ConfigLoader.movement.dash.iFrames;
    this.dashTimer = this.dashDuration;
    this.refillTimer = 0;

    return true;
  }

  /** Strip i-frames early (e.g., when the player attacks mid-dash), if configured to do so. */
  cancelIFrames(): void {
    if (ConfigLoader.movement.dash.iFramesCancelledByAttack) {
      this.hasIFrames = false;
    }
  }

  update(delta: number): void {
    // Update dash timer
    if (this.isDashing) {
      this.dashTimer -= delta;
      if (this.dashTimer <= 0) {
        this.isDashing = false;
        this.hasIFrames = false;
        this.framesSinceDashEnded = 0;
        DebugLog.log('physics', `DASH ended: charges=${this.charges}/${this.maxCharges}`);
      }
    } else if (this.framesSinceDashEnded < Infinity) {
      this.framesSinceDashEnded++;
    }

    // Charge refill
    if (this.charges < this.maxCharges && !this.isDashing) {
      const requiresFull = this.overrideRefillRequiresFullStamina ?? this.refillRequiresFullStamina;
      const canRefill = requiresFull
        ? this.stamina.isFull
        : true;

      if (canRefill) {
        this.refillTimer += delta;
        if (this.refillTimer >= this.refillTime) {
          this.charges++;
          this.refillTimer = 0;
        }
      } else {
        this.refillTimer = 0;
      }
    }

    this.events.emit('dash-changed', this.charges, this.maxCharges, this.refillProgress);
  }

  destroy(): void {
    // Nothing to clean up
  }
}
